import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'api/api-context';
import BookingLayout from 'pages/booking/BookingLayout';
import BookingNotFound from 'pages/booking/BookingNotFound';
import {
  AccountError,
  AccountSkeleton,
  BookingInterval,
  ReservationStatusBadge,
  clearBookingDraft,
  openCalendarBlob,
} from 'pages/booking/account/account-utils';
import { BOOKING_DETAIL_PATH, BOOKING_GUEST_MANAGE_STORED_PATH } from 'routes/paths';
import useLang from 'lang';
import { formatLocaleDateTime } from 'lang/date-time';
import 'pages/booking/account/booking-account.css';

const TOKEN_STORAGE_KEY = 'phillip.booking.guestManagementToken';

/**
 * Permanent, single-reservation self-service page for a guest.
 */
export default function GuestBookingManagement() {
  const route = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { getAction, setPublicBranding } = useApi();
  const { _t, lang } = useLang();
  const [token, setToken] = useState(() => route.token || sessionStorage.getItem(TOKEN_STORAGE_KEY) || '');
  const [reservation, setReservation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!route.token) return;
    sessionStorage.setItem(TOKEN_STORAGE_KEY, route.token);
    setToken(route.token);
    navigate(BOOKING_GUEST_MANAGE_STORED_PATH.original, { replace: true });
  }, [route.token, navigate]);

  const load = useCallback(async () => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const data = (await getAction('GetPublicBooking')({ parameters: { token } })) as any;
      setReservation(data.reservation);
      setPublicBranding({
        buildingId: data.reservation.building.id,
        buildingName: data.reservation.building.title,
        logoUrl: data.reservation.building.logoUrl || null,
      });
    } catch (requestError: any) {
      if (requestError.response?.status === 404) setNotFound(true);
      else setError(true);
    } finally {
      setLoading(false);
    }
  }, [getAction, setPublicBranding, token]);

  useEffect(() => {
    void load();
    if (!reservation || reservation.status !== 'waiting-for-payment') return undefined;
    const interval = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(interval);
  }, [load, reservation?.status]);

  useEffect(
    () => () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl]
  );

  /**
   * Opens the invoice inside this page without exposing a permanent public URL.
   */
  const previewInvoice = async () => {
    const blob = await getAction('GetPublicBookingInvoicePdf')({ parameters: { token } });
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(URL.createObjectURL(blob));
  };

  /**
   * Fetches the guest-authorized privacy-safe calendar event.
   */
  const addToCalendar = async () => {
    const blob = await getAction('GetPublicBookingCalendar')({ parameters: { token } });
    openCalendarBlob(blob, reservation.id);
  };

  if (notFound) return <BookingNotFound />;
  return (
    <BookingLayout>
      <div className="account-page">
        <header className="account-heading">
          <h1>{_t('Manage reservation')}</h1>
          <p>{_t('This private page shows only this guest reservation.')}</p>
        </header>
        {loading && !reservation && <AccountSkeleton />}
        {error && <AccountError retry={() => void load()} />}
        {reservation && (
          <article className="account-detail-card">
            <div className="account-card-heading">
              <div>
                <p className="account-eyebrow">
                  {_t('Reservation')} #{reservation.id}
                </p>
                <h2>{reservation.space.name}</h2>
                <p>{reservation.building.title}</p>
              </div>
              <ReservationStatusBadge status={reservation.status} />
            </div>
            <p className="account-booking-time">
              <BookingInterval startsAt={reservation.startsAt} endsAt={reservation.endsAt} />
            </p>
            {reservation.temporaryPin && (
              <section className="account-finance-card">
                <h3>{_t('Temporary PIN')}</h3>
                <strong className="booking-pin-code">{reservation.temporaryPin.pinCode}</strong>
                <p>
                  {_t('Valid from')}: {formatLocaleDateTime(reservation.temporaryPin.validFrom, lang)}
                  <br />
                  {_t('Valid until')}: {formatLocaleDateTime(reservation.temporaryPin.validTo, lang)}
                </p>
                <p>{_t('The PIN is shown only while it remains valid for this accepted reservation.')}</p>
              </section>
            )}
            {reservation.payment?.paymentGateway === 'manual' && reservation.status === 'waiting-for-payment' && (
              <section className="account-finance-card">
                <h3>{_t('Payment instructions')}</h3>
                {reservation.payment.invoice?.payBySquareQr && (
                  <img
                    className="account-qr"
                    src={reservation.payment.invoice.payBySquareQr}
                    alt={_t('PAY by square')}
                  />
                )}
                <p>
                  {_t('IBAN')}: {reservation.payment.invoice?.iban}
                </p>
                <p>
                  {_t('Variable symbol')}: {reservation.payment.invoice?.variableSymbol}
                </p>
              </section>
            )}
            <div className="account-actions">
              <button
                className="account-button account-button--secondary"
                disabled={loading}
                onClick={() => void load()}
              >
                {_t('Refresh status')}
              </button>
              {reservation.actions.canResumePayment && (
                <button
                  className="account-button"
                  onClick={() => window.location.assign(reservation.payment.attempt.checkoutUrl)}
                >
                  {_t('Continue to payment')}
                </button>
              )}
              {reservation.actions.canPreviewInvoice && (
                <button className="account-button account-button--secondary" onClick={() => void previewInvoice()}>
                  {_t('View invoice')}
                </button>
              )}
              {reservation.actions.canAddToCalendar && (
                <button className="account-button account-button--secondary" onClick={() => void addToCalendar()}>
                  {_t('Add to calendar')}
                </button>
              )}
              {reservation.actions.canBookAgain && (
                <button
                  className="account-button account-button--secondary"
                  onClick={() => {
                    clearBookingDraft();
                    navigate(BOOKING_DETAIL_PATH.insert({ slug: reservation.space.slug }));
                  }}
                >
                  {_t('Book again')}
                </button>
              )}
            </div>
            {pdfUrl && <iframe className="account-pdf" title={_t('Invoice preview')} src={pdfUrl} />}
          </article>
        )}
      </div>
    </BookingLayout>
  );
}
